import { gettext } from '@eventbrite/i18n';
import { LoadingSkeleton } from '@eventbrite/marketing-hub-ui';
import { getLayer, isStatsigInitialized } from '@eventbrite/statsig';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import { LISTING_PAGE_ALL_IN_PRICE_EXPERIMENT } from '../../../../constants';
import { AllInPriceExperimentValues } from '../../../../types';
import { TicketClass } from './models';
import {
    calculateTotalMinMaxPrice,
    fetchTicketClassesInformation,
    formatPrice,
} from './utils';

type PanelDisplayPriceProps = {
    currency: string;
    eventId: string;
    locale: string;
    priceDisplay: string;
};

const PanelDisplayFormattedPrice = ({
    allInPriceExperimentValue,
    currency,
    locale,
    ticketClasses,
}: Pick<PanelDisplayPriceProps, 'currency' | 'locale'> & {
    allInPriceExperimentValue: AllInPriceExperimentValues;
    ticketClasses: TicketClass[];
}) => {
    const allInPriceWithFeeLabel =
        allInPriceExperimentValue ===
        LISTING_PAGE_ALL_IN_PRICE_EXPERIMENT.variantB;
    const { min, max } = calculateTotalMinMaxPrice(ticketClasses);
    const formatPriceFn = formatPrice({
        currency,
        locale: locale.replace('_', '-'),
    });

    const formattedPrice =
        min === max
            ? formatPriceFn(min)
            : `${formatPriceFn(min)} - ${formatPriceFn(max)}`;

    return (
        <>
            <p
                className={cx({
                    'eds-l-mar-bot-2': !allInPriceWithFeeLabel,
                })}
            >
                {formattedPrice}
            </p>
            {allInPriceWithFeeLabel ? (
                <p
                    className="eds-text-bs eds-text-color--ui-700"
                    style={{ fontWeight: 400 }}
                >
                    {gettext('Fees and taxes included')}
                </p>
            ) : null}
        </>
    );
};

export const PanelDisplayPrice = ({
    currency,
    eventId,
    locale,
    priceDisplay,
}: PanelDisplayPriceProps) => {
    const allInPriceExperimentValue = getLayer(
        LISTING_PAGE_ALL_IN_PRICE_EXPERIMENT.layer,
        LISTING_PAGE_ALL_IN_PRICE_EXPERIMENT.param,
        LISTING_PAGE_ALL_IN_PRICE_EXPERIMENT.defaultValue,
    );

    const isAllInPriceControlVariant =
        allInPriceExperimentValue ===
        LISTING_PAGE_ALL_IN_PRICE_EXPERIMENT.defaultValue;

    const [ticketClasses, setTicketClasses] = useState<TicketClass[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const shouldRenderControlFallback =
        isAllInPriceControlVariant ||
        error ||
        (!loading && !ticketClasses.length);

    useEffect(() => {
        if (isAllInPriceControlVariant) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await fetchTicketClassesInformation(eventId);
                setTicketClasses(response.ticketClasses);
            } catch (error) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!isStatsigInitialized()) return null;

    if (shouldRenderControlFallback) {
        return <div className="eds-l-mar-bot-2">{priceDisplay}</div>;
    }

    if (loading) {
        return (
            <LoadingSkeleton width="100%" height="42px" borderRadius="8px" />
        );
    }

    return (
        <PanelDisplayFormattedPrice
            allInPriceExperimentValue={
                allInPriceExperimentValue as AllInPriceExperimentValues
            }
            currency={currency}
            locale={locale}
            ticketClasses={ticketClasses}
        />
    );
};
